/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { Text } from '../UNSAFE_Text';
import { useButtonLabelLayoutTheme } from '#hooks/PRIVATE_useButtonLabelLayoutTheme';

export type ButtonDisplay = 'icons' | 'label' | 'all';
export type LayoutSizes =
  | ComponentProps<typeof BaseButton>['size']
  | 'embeddedxs'
  | 'embeddedsm'
  | 'embeddedmd'
  | 'embeddedlg';

export type Props = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   *The suffix is appended to the text label, used for menu buttons.
   */
  suffix?: string;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Styling can be used for customizing icon layout by limiting padding ("embedded")
   * The label can fill available width ("fill").
   */
  styling?: 'default' | 'embedded' | 'fill';

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof BaseButton>['size'];

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: ButtonDisplay;
};

export function ButtonLabelLayout({
  size = 'md',
  display = 'all',
  styling = 'default',
  ...props
}: Props) {
  const {
    startIconClasses,
    labelClasses,
    suffixClasses,
    endIconClasses,
    containerClasses,
    isShowIcons,
    isShowText
  } = useButtonLabelLayoutTheme({ display, size, styling, ...props });

  return (
    <span class={containerClasses}>
      {isShowIcons && props.startIcon && <span class={startIconClasses}>{props.startIcon}</span>}
      {isShowText && props.children && <span class={labelClasses}> {props.children} </span>}
      {isShowText && props.suffix && (
        <span class={suffixClasses}>
          <Text truncation={'ellipsis'} weight={'inherit'}>
            {props.suffix}
          </Text>
        </span>
      )}
      {isShowIcons && props.endIcon && <span class={endIconClasses}>{props.endIcon}</span>}
    </span>
  );
}

ButtonLabelLayout.displayName = 'ButtonLabelLayout';
